/**
 * 34 - Mine the Road (10 §2): lay a charge at each of three marked culverts under the road before the enemy
 * column comes down it - the arrival time is on the HUD from touchdown. As mission 12 the charges wait and the
 * mission completes on the laying - but here they wait for the COLUMN (`detonateUnder`): each goes off as the
 * column's men come over it, and what it kills is his. He must not be SEEN within 20 m of any culvert; the
 * intended way is the uniform from the way station's guard house. The culverts are 26 m apart
 * down the road - one north of the way station, one at its south side, one out in the belt - so each is its own
 * approach, with its own watched circle.
 *
 * ROUND4 §1: the column is LORRIES. They come down the road from the north with their sections aboard, roll
 * over every culvert - which is where the charges go off, under them - and drive off the south end of their
 * run instead of turning round for ever. Its arrival time comes from the measured route (helpers3aRoute.ts).
 */

import { roadDistance } from '../../sim/statics';
import type { ObjectiveRule } from '../../sim/types';
import { buildRun } from '../obstacles';
import {
  checker, clutter, countKind, guard, guardAtDoor, guardsAbout, required, ruleOf, structure, uniformDonor,
} from './helpers';
import { ON_ROAD, lamp, put, roadXAt } from './helpers3a';
import { checkRuleText, checkTargets, timedRules, waiting } from './helpers3aAsserts';
import { deadlineSeconds, measureRoute, retime } from './helpers3aRoute';
import { runPasses, vehicle, vehiclesOf } from './helpers4Vehicles';
import type { ArchetypeSpec } from './types';

export const CULVERTS = 3;
export const SEEN_RADIUS = 20;
/** Lorries in the column, and the section riding in each. */
export const columnFor = (threat: number): number => 3 + (threat >= 5 ? 1 : 0) + (threat >= 8 ? 1 : 0);
export const LOAD = 2;
/** The column rolls at this, in metres per second: slow enough that a charge under it catches the lorry behind. */
const COLUMN_SPEED = 5;

/** Culvert to culvert down the road: more than the watched radius, so no two circles are one problem. */
const APART = 26;
/** Seconds allowed at each culvert when the column's arrival is set. */
const WORK = 25;
const TAG = 'convoyCrosses';

export const mineRoad: ArchetypeSpec = {
  id: 'mine-road',
  name: 'MINE THE ROAD',
  nativeBiome: 'desert',
  baseThreat: 3,
  lengthTiles: 160,
  clockScale: 1,
  startAlert: 0,
  garrison: 'regular',
  // A way station on an open road: no wire, and the road runs on through it to the north edge.
  perimeter: 'none',
  sealed: false,
  layout: 'compound',
  size: { w: [42, 48], d: [34, 34] },
  river: false,
  patrolLoop: 'inside',
  breach: { min: 0, max: 0 },

  build(g) {
    const end = g.ctx.plan.roadEnd;
    g.map.road([[end.x, end.z + 1], [end.x, 0]], 4.2);
    const north = g.rect.z0 + 4;
    const culverts = Array.from({ length: CULVERTS }, (_, i) => {
      const z = north + i * APART;
      return put(g, 'culvert', roadXAt(g, z), z, { target: true, margin: 0.2, avoid: ON_ROAD });
    });

    // R4: the column's lane down the road, reserved before the way station is built across it.
    const south = (g.ctx.plan.lines[g.ctx.plan.lines.length - 1]?.z ?? north + 2 * APART + 10) - 6;
    const away = { x: roadXAt(g, south), z: Math.max(south, north + 2 * APART + 4) };
    const run = [{ x: roadXAt(g, 2), z: 2 }, ...culverts.map((c) => ({ x: c.x, z: c.z })), away];
    for (let i = 0; i < columnFor(g.threat); i++) {
      vehicle(g, { kind: 'truck', route: run, wakeTag: TAG, carries: LOAD, speed: COLUMN_SPEED, back: 8 * i });
    }

    // The way station: a sandbag chicane on the road, the guard house beside it, quarters and stores behind.
    const mid = Math.round((g.rect.z0 + g.rect.z1) / 2) + 3;
    const east = end.x < (g.rect.x0 + g.rect.x1) / 2 ? 1 : -1;
    buildRun(g.map, 'sandbags', mid, end.x - 5, end.x - 0.4, []);
    buildRun(g.map, 'sandbags', mid + 5, end.x + 0.4, end.x + 5, []);
    const beside = { ...g.rect, ...(east > 0 ? { x0: end.x + 6 } : { x1: end.x - 6 }), z0: mid - 8, z1: mid + 10 };
    const house = required(g, 'guardHouse', { within: beside, margin: 1.4 });
    structure(g, g.kit.shelter, { within: { ...beside, z0: g.rect.z0 + 8, z1: g.rect.z1 - 2 }, margin: 1.2 });
    structure(g, 'alarmPost', { within: beside, margin: 1 });
    if (g.threat >= 4) lamp(g, end.x + east * 5, mid - 4);
    clutter(g, 'crate', 3);
    guardAtDoor(g, house);
    // The checkpoint's man on the road, beside the middle culvert: on every map there is someone to be seen by.
    const middle = culverts[1];
    const onRoad = middle ? guard(g, middle.x + 2.5, middle.z - 2, 0) : null;
    if (!onRoad) g.map.problems.push('mine-road: nowhere for the road sentry');
    guardsAbout(g, 1 + g.rng.int(2));

    const seen = culverts.map((c): ObjectiveRule => ({
      type: 'notSeenNear', propId: c.id, radius: SEEN_RADIUS,
      orders: `DO NOT BE SEEN WITHIN ${SEEN_RADIUS} M OF ANY CULVERT, BEFORE OR AFTER: THE ROAD WOULD BE SWEPT`,
      reason: 'SEEN AT THE CULVERT - THE ROAD WILL BE SWEPT',
    }));
    return {
      objective: {
        kind: 'plantAtTargets', label: 'MINE THE CULVERTS', targetPropIds: culverts.map((c) => c.id), minFraction: 1,
        rules: [
          { type: 'bombsHoldUntilMidnight', detonateUnder: 'convoyCrosses' },
          ...seen,
          {
            type: 'timedEvent', atSeconds: 420, effect: 'convoyCrosses', wakes: TAG, failIfIncomplete: true,
            orders: 'THE COLUMN COMES DOWN THE ROAD ON THE CLOCK: EVERY CULVERT MUST BE MINED BEFORE IT DOES',
            warning: 'THE COLUMN ARRIVES IN',
            reason: 'THE COLUMN PASSED UNHARMED',
          },
          { type: 'alertConsequence', level: 3, delaySeconds: 0, effect: 'reinforceTarget' },
          { type: 'recall', mode: 'normal' },
        ],
      },
      finish(def) {
        const route = measureRoute(def);
        if (route) retime(def, (r) => r.effect === 'convoyCrosses', deadlineSeconds(def, route, WORK * CULVERTS));
      },
    };
  },

  asserts(def, probe) {
    const { check, out } = checker('mine-road');
    const culverts = checkTargets(check, def, ['culvert']);
    check(culverts.length === CULVERTS, `needs ${CULVERTS} culverts, has ${culverts.length}`);
    check(def.objective.kind === 'plantAtTargets' && def.objective.minFraction === 1, 'a charge at every culvert');
    check(ruleOf(def, 'bombsHoldUntilMidnight')?.detonateUnder === 'convoyCrosses', 'the charges must wait for the column');
    const watched = def.objective.rules.filter((r) => r.type === 'notSeenNear');
    for (const c of culverts) {
      check(roadDistance(def, c.x, c.z) <= 0, `culvert ${c.id} is not under the road`);
      check(probe.breach(c.id) === 0, `culvert ${c.id} cannot be walked to`);
      check(watched.some((r) => r.type === 'notSeenNear' && r.propId === c.id && r.radius === SEEN_RADIUS),
        `being seen within ${SEEN_RADIUS} m of culvert ${c.id} must matter`);
      for (const o of culverts) {
        const d = Math.sqrt((c.x - o.x) * (c.x - o.x) + (c.z - o.z) * (c.z - o.z));
        const pair = `culverts ${c.id} and ${o.id}`;
        if (c.id < o.id) check(d >= SEEN_RADIUS + 4, `${pair} are only ${d.toFixed(1)} m apart: one approach, not two`);
      }
    }
    // The rule must be live: a man who holds a post inside some culvert's watched circle.
    const posted = def.spawns
      // ROUND8: a dog sees nobody - it smells - so it is no watcher of a culvert.
      .filter((sp) => sp.dormantUntil === undefined && sp.patrol === undefined && sp.mans === undefined
        && sp.kind !== 'dog');
    const inSight = (c: { x: number; z: number }, sp: { x: number; z: number }): boolean =>
      (sp.x - c.x) * (sp.x - c.x) + (sp.z - c.z) * (sp.z - c.z) <= SEEN_RADIUS * SEEN_RADIUS;
    check(culverts.some((c) => posted.some((sp) => inSight(c, sp))),
      'nobody is posted within sight of any culvert: the not-seen rule would never bite');
    check(countKind(def, 'guardHouse') >= 1, 'needs a guard house: the uniform is the intended way');
    check(uniformDonor(def, 'guardHouse'), 'a guard must stand at the guard house: his is the uniform to take');

    const [column] = timedRules(def, 'convoyCrosses');
    check(column !== undefined && column.failIfIncomplete === true && (column.from ?? 'touchdown') === 'touchdown',
      'the column arrives on a clock from touchdown, and the job must be done by then');
    if (column) {
      const share = column.atSeconds / def.clockSeconds;
      check(
        share >= 0.5 && share <= 0.8,
        `the column arrives at ${column.atSeconds} s of a ${def.clockSeconds} s clock`,
      );
    }
    const lorries = vehiclesOf(waiting(def, column?.wakes ?? TAG));
    check(lorries.length === columnFor(def.threat), `the column numbers ${columnFor(def.threat)} lorries, not ${lorries.length}`);
    check(vehiclesOf(def.spawns).length === lorries.length, 'every vehicle of this mission belongs to the column');
    // The world agrees with the text: the column really drives down the road, over every culvert, and away.
    for (const lorry of lorries) {
      const nodes = lorry.route?.points.points ?? [];
      check(lorry.kind === 'truck' && lorry.carries === LOAD, `a lorry of the column carries ${LOAD} men`);
      check(lorry.route?.loop === false, 'the column must drive off the map, not turn round for ever');
      check(nodes.every((n) => roadDistance(def, n[0], n[1]) <= 3), 'the column must keep to the road');
      check(culverts.every((c) => runPasses(lorry, c.x, c.z, 2)), 'the column must roll over every culvert');
    }
    checkRuleText(check, def);
    return out;
  },
};
